import React, { useContext, useEffect, useState } from 'react';

import { ISkleraItem } from '../utils/interface';
import DropDown from './DownloadButton/DropDown';
import { Context } from '../MunicipalNoticeBoard';
import { skleraSDK } from '@sklera/sdk';
import { HumanReadableDate } from '../utils/HumanReadableDate';
import styled, { ThemedStyledProps } from 'styled-components';
import { transparentize } from 'polished';
import { faArrowLeft, faArrowRight, faClose } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { shadowBorderString } from '../mainCategory/StyledComponents';

export const ViewerContainer = styled.div`
  background-color: ${(props) => props.theme.secondColor};
`;
export const Paragraph = styled.p`
  display: inline;
  padding: 0;
  margin: 0 0.5em;
  font-weight: 600;
  font-style: italic;
  color: ${(props) => props.theme.mainColor};
`;
export const Heading = styled.h2`
  margin: 0;
  font-weight: 600;
  font-size: 2em;
  max-width: 27vw;
  overflow-wrap: break-word;
  line-height: 1;
  color: ${(props) => props.theme.mainColor};
`;

export const Description = styled.p`
  margin: 0;
  max-width: 27vw;
  overflow-wrap: break-word;
  line-height: 1;
  font-weight: 400;
  font-size: 1.2em;
  color: ${(props) => props.theme.mainColor};
`;

export const Button = styled.button`
    background: ${(props) => props.theme.secondColor};
    color: ${(props) => props.theme.mainColor};
    border-radius: 0.5em;
    margin: 0;
    padding: 0.6em 1em;
    font-size: 1.2em;
    ${(props) => shadowBorderString(props)}
`;

const Viewer = ({
    selectedItem,
    closePdfViewer,
}: {
    selectedItem: ISkleraItem | null;
    closePdfViewer: () => void;
}): JSX.Element => {
    const [pageNumber, setPageNumber] = useState<number>(0);
    const { fullscreenSwitch, menuPosition, font, isPublicApp } = useContext(Context);
    const [loading, setLoading] = useState(false);
    const fontFamily = font ? { fontFamily: font  } : {};

    const visible = !!selectedItem;

    useEffect(() => {
        if (selectedItem) {
            skleraSDK.logEvent('TOUCH_ACTION_SHOW_ELEMENT', { itemId: selectedItem._id }, (re: any) => {});
        }
    }, [selectedItem, selectedItem?._id]);

    useEffect(() => {
        if (fullscreenSwitch) {
            visible ? skleraSDK.setFullscreen() : skleraSDK.unsetFullscreen();
        }
        return () => {
            fullscreenSwitch && skleraSDK.unsetFullscreen();
        };
    }, [fullscreenSwitch, visible]);

    if (!visible) {
        return <div />;
    }

    const maxPageNumber = selectedItem.pages.length || 0;

    function changePageNumber(newPageNumber: number) {
        if (newPageNumber >= 0 && maxPageNumber && newPageNumber < maxPageNumber) {
            setLoading(true);
            setPageNumber(newPageNumber);
        }
    }

    return (
        <ViewerContainer
            className="viewer-container"
            style={{
                display: 'flex',
                flexDirection:
                    menuPosition === 'center' ? 'row' : menuPosition === 'bottom' ? 'column' : 'column-reverse',
            }}>
            <div
                style={{
                    width: menuPosition === 'center' ? '72%' : '100%',
                    height: menuPosition === 'center' ? '100%' : '85%',
                    margin: menuPosition === 'bottom' ? '0 0 2em 0' : '0',
                    float: 'left',
                    textAlign: 'center',
                }}>
                <div
                    style={{
                        width: menuPosition === 'center' ? '70%' : '100%',
                        height: menuPosition === 'center' ? '100%' : '70%',
                        position: 'absolute',
                        zIndex: 1000,
                    }}>
                    <div
                        style={{ width: '53%', height: '100%', zIndex: 10, float: 'left' }}
                        onClick={() => changePageNumber(pageNumber - 1)}
                    />
                    <div
                        style={{ width: '47%', height: '100%', zIndex: 10, float: 'left' }}
                        onClick={() => changePageNumber(pageNumber + 1)}
                    />
                </div>

                <img
                    style={{ maxWidth: menuPosition === 'center' ? '64vw' : '90vw' }}
                    className={'pdf-viewer' + (loading ? ' loading' : '')}
                    src={selectedItem.pages[pageNumber].url}
                    alt=""
                    onLoad={() => {
                        setLoading(false);
                    }}
                />
            </div>
            <div
                style={{
                    width: menuPosition === 'center' ? '28%' : '100%',
                    height: menuPosition === 'center' ? '100% ' : '15%',
                    float: 'left',
                    zIndex: 20,
                    display: 'flex',
                    flexDirection: menuPosition === 'center' ? 'column' : 'row-reverse',
                    justifyContent: menuPosition === 'center' ? 'center' : 'space-between',
                }}>
                <div className="header">
                    <Button onClick={closePdfViewer} style={{ margin: "0 0.5em 0 0"}}>
                        <FontAwesomeIcon icon={faClose} />
                        <Paragraph style={fontFamily}>Schließen</Paragraph>
                    </Button>
                    {selectedItem ? (
                        <DropDown downloadItem={selectedItem} isPublicApp={!!isPublicApp} />
                    ) : null}
                </div>
                <div>
                    <Button
                        style={{ margin: "0 0.5em 0 0"}}
                        onClick={() => changePageNumber(pageNumber - 1)}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                        <Paragraph style={fontFamily}>Seite zurück</Paragraph>
                    </Button>
                    <Button
                        onClick={() => changePageNumber(pageNumber + 1)}>
                        <Paragraph style={fontFamily}>Seite vor</Paragraph>
                        <FontAwesomeIcon icon={faArrowRight} />
                    </Button>
                    <div style={{display: "flex", flexDirection: "column"}}>
                        <Paragraph style={fontFamily}>{`Seite ${pageNumber + 1} von ${maxPageNumber}`}</Paragraph>
                        <Paragraph style={fontFamily}>
                            <HumanReadableDate
                                validFrom={selectedItem.validFrom}
                                validTo={selectedItem.validTo}></HumanReadableDate>
                        </Paragraph>
                    </div>
                </div>
                <div>
                    <Heading style={{ overflowWrap: 'break-word', margin: '0', ...fontFamily }} className="line-clamp">
                        {selectedItem.name}
                    </Heading>
                    {selectedItem?.description ? (
                        <Description
                            style={{ overflowWrap: 'break-word', ...fontFamily }}
                            className="line-clamp">
                            {selectedItem?.description}
                        </Description>
                    ) : null}
                </div>
            </div>
        </ViewerContainer>
    );
};

export default Viewer;
